/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog } from '@coursera/cds-core';

import DefineGoalScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/DefineGoalScreen';
import DiscardScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/DiscardScreen';
import EditLearnerListScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/EditLearnerListScreen';
import GeneratingScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/GeneratingScreen';
import LearnerListScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/LearnerListScreen';
import ManualDraftScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/ManualDraftScreen';
import MessageSentScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/MessageSentScreen';
import MessageStepper from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/MessageStepper';
import ReviewAndSendScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/ReviewAndSendScreen';
import type {
  AIMessageDraft,
  AIMessageScreen,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import PreviewEmailScreen from 'bundles/enterprise-admin-messaging-v2/components/common/PreviewEmailScreen';
import type { PreviewReturnScreen } from 'bundles/enterprise-admin-messaging-v2/utils/constants';
import { PREVIEW_RETURN_SCREEN } from 'bundles/enterprise-admin-messaging-v2/utils/constants';
import { mockGenerateDraft } from 'bundles/enterprise-admin-messaging-v2/utils/mockGenerate';
import { getDefaultAiCohort } from 'bundles/enterprise-admin-messaging-v2/utils/mockLearners';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  open: boolean;
  initialPrompt: string;
  onClose: () => void;
  /**
   * How long each of the two Generating loading stages runs. Exposed so
   * tests (and Storybook) can short-circuit the timer.
   */
  generatingStageDurationMs?: number;
};

// Mirror Dialog.HeadingGroup's horizontal padding so the stepper aligns with
// the title's left edge instead of receiving the wider Dialog.Content padding.
const stepperSlot = css`
  padding: 0 var(--cds-spacing-300);
`;

// Override the CDS Dialog's defaults to match the design spec:
//   - Width fixed at 864px (CDS `width="large"` is wider than the design).
//   - Max-height bumped from CDS's default 80vh to 85vh so Review-and-send
//     fits without an internal scrollbar on standard 1080p displays.
// The css prop composes after CDS's internal styles, so a same-specificity
// override wins.
const dialogSize = css`
  .cds-Dialog-dialog {
    width: 864px;
    max-width: 864px;
    max-height: 85vh;
  }
`;

// Both learner screens need more vertical room than the default 85vh cap
// from `dialogSize` so the table + pagination + actions all fit without
// scrolling. Composed on top of dialogSize for both viewer and editor.
const dialogLearnerCap = css`
  .cds-Dialog-dialog {
    max-height: 95vh;
  }
`;

// Additional override applied only to the Learner list (viewer) so the
// modal stays a stable 872px regardless of how many rows the filter shows.
// The editor stays hug-content (with the 95vh cap above).
const dialogLearnerViewerHeight = css`
  .cds-Dialog-dialog {
    height: 872px;
  }
`;

const buildEmptyDraft = (): AIMessageDraft => {
  const cohort = getDefaultAiCohort();
  return {
    prompt: '',
    audienceSummary: '',
    audienceCount: 0,
    subject: '',
    message: '',
    ctaText: '',
    ctaUrl: '',
    autoSendEnabled: false,
    autoSendCadence: '15_days',
    learners: cohort,
    originalLearners: cohort,
    isManuallyEdited: false,
  };
};

const titleForScreen = (screen: AIMessageScreen): string => {
  switch (screen) {
    case 'sent':
      return _t('Message sent successfully');
    case 'review':
      return _t('Review and send');
    case 'manual':
      return _t('Draft a message manually');
    case 'discard':
      return _t('Discard this message?');
    case 'preview':
      return _t('Preview and send');
    case 'learner-list':
      return _t('Learner list');
    case 'edit-learner-list':
      return _t('Add or remove learners');
    case 'define':
    case 'generating':
    default:
      return _t('Create a message');
  }
};

/**
 * Top-level dialog. Owns the active sub-screen + draft state, and dispatches
 * to the four sub-screens. Reset every time the modal re-opens so a fresh
 * `initialPrompt` always starts from Step 1.
 */
const AIMessageModal = ({ open, initialPrompt, onClose, generatingStageDurationMs }: Props) => {
  const [activeScreen, setActiveScreen] = React.useState<AIMessageScreen>('define');
  const [draft, setDraft] = React.useState<AIMessageDraft>(() => buildEmptyDraft());
  // Preview can be reached from Review (AI flow) or Manual; its Back button
  // returns to whichever screen opened it.
  const [previewReturnScreen, setPreviewReturnScreen] = React.useState<PreviewReturnScreen>(
    PREVIEW_RETURN_SCREEN.REVIEW
  );
  // Closing mid-draft swaps to the 'discard' confirmation screen; this remembers
  // which screen to restore if the admin chooses "Keep editing".
  const [screenBeforeDiscard, setScreenBeforeDiscard] = React.useState<AIMessageScreen>('define');

  // Reset the modal to a clean Step 1 state whenever it opens. Using
  // `open` as the trigger keeps the previous draft visible while the
  // close animation plays.
  React.useEffect(() => {
    if (open) {
      setActiveScreen('define');
      setDraft({ ...buildEmptyDraft(), prompt: initialPrompt });
    }
  }, [open, initialPrompt]);

  const updateDraft = React.useCallback((partial: Partial<AIMessageDraft>) => {
    setDraft((previous) => ({ ...previous, ...partial }));
  }, []);

  const handleGenerate = React.useCallback(() => {
    setActiveScreen('generating');
  }, []);

  const handleGenerateComplete = React.useCallback(() => {
    // TODO: replace `mockGenerateDraft` with the real generate-message GraphQL
    // mutation once the backend lands. The orchestrator pattern stays the
    // same; only the data source swaps out.
    setDraft((previous) => ({ ...previous, ...mockGenerateDraft(previous.prompt) }));
    setActiveScreen('review');
  }, []);

  const handleSend = React.useCallback(() => {
    setActiveScreen('sent');
  }, []);

  const handleBackToDefine = React.useCallback(() => {
    setActiveScreen('define');
  }, []);

  const handleDraftManually = React.useCallback(() => {
    setActiveScreen('manual');
  }, []);

  const handlePreviewEmail = React.useCallback(() => {
    setPreviewReturnScreen(PREVIEW_RETURN_SCREEN.REVIEW);
    setActiveScreen('preview');
  }, []);

  const handlePreviewManually = React.useCallback(() => {
    setPreviewReturnScreen(PREVIEW_RETURN_SCREEN.MANUAL);
    setActiveScreen('preview');
  }, []);

  const handleViewLearnerList = React.useCallback(() => {
    setActiveScreen('learner-list');
  }, []);

  const handleBackToReview = React.useCallback(() => {
    setActiveScreen('review');
  }, []);

  const handleEditLearnerList = React.useCallback(() => {
    setActiveScreen('edit-learner-list');
  }, []);

  const handleSaveLearnerList = React.useCallback(
    (nextLearners: Learner[]) => {
      // Keep audienceCount in sync with the edited cohort — the Sent screen
      // reads draft.audienceCount, and Review shows learners.length once edited.
      updateDraft({ learners: nextLearners, audienceCount: nextLearners.length, isManuallyEdited: true });
      setActiveScreen('review');
    },
    [updateDraft]
  );

  const handleResetLearnerList = React.useCallback(() => {
    setDraft((previous) => ({
      ...previous,
      learners: previous.originalLearners,
      audienceCount: previous.originalLearners.length,
      isManuallyEdited: false,
    }));
    setActiveScreen('learner-list');
  }, []);
  const handleBackFromPreview = React.useCallback(() => {
    setActiveScreen(previewReturnScreen);
  }, [previewReturnScreen]);

  // Every dismiss path (X, Esc, overlay, Cancel) routes through here. The Sent
  // screen has nothing left to lose, so it closes immediately; on the discard
  // screen itself another dismiss means "keep editing"; every other screen
  // swaps to the discard confirmation first.
  const handleRequestClose = React.useCallback<() => void>(() => {
    if (activeScreen === 'sent') {
      onClose();
    } else if (activeScreen === 'discard') {
      setActiveScreen(screenBeforeDiscard);
    } else {
      setScreenBeforeDiscard(activeScreen);
      setActiveScreen('discard');
    }
  }, [activeScreen, screenBeforeDiscard, onClose]);

  const handleKeepEditing = React.useCallback<() => void>(() => {
    setActiveScreen(screenBeforeDiscard);
  }, [screenBeforeDiscard]);

  // Unmount the dialog entirely when closed so the modal title doesn't leak
  // into the document and the orchestrator state resets fresh on the next
  // open.
  if (!open) {
    return null;
  }

  let body: React.ReactNode = null;
  switch (activeScreen) {
    case 'define':
      body = (
        <DefineGoalScreen
          prompt={draft.prompt}
          onPromptChange={(value) => updateDraft({ prompt: value })}
          onGenerate={handleGenerate}
          onCancel={handleRequestClose}
          onDraftManually={handleDraftManually}
        />
      );
      break;
    case 'generating':
      body = <GeneratingScreen onComplete={handleGenerateComplete} stageDurationMs={generatingStageDurationMs} />;
      break;
    case 'review':
      body = (
        <ReviewAndSendScreen
          draft={draft}
          updateDraft={updateDraft}
          onSend={handleSend}
          onBack={handleBackToDefine}
          onPreviewEmail={handlePreviewEmail}
          onViewLearnerList={handleViewLearnerList}
        />
      );
      break;
    case 'learner-list':
      body = <LearnerListScreen learners={draft.learners} onEdit={handleEditLearnerList} onBack={handleBackToReview} />;
      break;
    case 'edit-learner-list':
      body = (
        <EditLearnerListScreen
          initialLearners={draft.learners}
          onSave={handleSaveLearnerList}
          onResetToOriginal={handleResetLearnerList}
        />
      );
      break;
    case 'manual':
      body = (
        <ManualDraftScreen
          draft={draft}
          updateDraft={updateDraft}
          onSend={handleSend}
          onDraftWithAI={handleBackToDefine}
          onPreviewEmail={handlePreviewManually}
        />
      );
      break;
    case 'preview':
      body = <PreviewEmailScreen draft={draft} onSend={handleSend} onBack={handleBackFromPreview} />;
      break;
    case 'sent':
      body = <MessageSentScreen draft={draft} updateDraft={updateDraft} onDone={onClose} />;
      break;
    case 'discard':
      body = <DiscardScreen onDiscard={onClose} onKeepEditing={handleKeepEditing} />;
      break;
    default:
      body = null;
  }

  const isLearnerScreen = activeScreen === 'learner-list' || activeScreen === 'edit-learner-list';
  const isLearnerListViewer = activeScreen === 'learner-list';

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      width="large"
      css={[dialogSize, isLearnerScreen && dialogLearnerCap, isLearnerListViewer && dialogLearnerViewerHeight]}
      data-testid="ai-message-modal"
    >
      <Dialog.HeadingGroup>{titleForScreen(activeScreen)}</Dialog.HeadingGroup>
      {(activeScreen === 'define' || activeScreen === 'review') && (
        <div css={stepperSlot}>
          <MessageStepper activeStep={activeScreen === 'define' ? 1 : 2} />
        </div>
      )}
      {body}
    </Dialog>
  );
};

export default AIMessageModal;
